import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import {
  HOME_V2_COMMENT_ICON_HEIGHT,
  HOME_V2_COMMENT_ICON_WIDTH,
} from "scmp-app/components/home-v2/constants";
import type { homeSuperSplashLivePrimaryV2ContentItemContent$key } from "scmp-app/queries/__generated__/homeSuperSplashLivePrimaryV2ContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Headline,
  HeadlineContainer,
  LiveTagContainer,
  StyledEntityLink,
  StyledLinkHighlightArea,
  SubHeadline,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: homeSuperSplashLivePrimaryV2ContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment homeSuperSplashLivePrimaryV2ContentItemContent on Content {
        entityId
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize540x360: true
            withCoverImageSize1200x800: true
            withCoverImageLeadingSlides: true
            withHeadline: true
            withLiveTag: true
            withSubHeadline: true
            withActionBar: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content);

  return (
    <Container className={className} ref={ref}>
      <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size1200x800",
              mobileUp: "size540x360",
              tabletUp: "size1200x800",
            },
            withCaption: false,
            withCarousel: { imageType: "default" },
          })}
        </CoverImage>
      </StyledEntityLink>
      <Content>
        <StyledEntityLink reference={content}>
          <StyledLinkHighlightArea>
            <HeadlineContainer>
              <LiveTagContainer>{providers.liveTag({ variant: "homeV2" })}</LiveTagContainer>
              <Headline>
                {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
              </Headline>
            </HeadlineContainer>
            <SubHeadline>{providers.subHeadline()}</SubHeadline>
          </StyledLinkHighlightArea>
        </StyledEntityLink>
        <ActionBar>
          {providers.actionBar({
            commentColorVariant: "light-action-blue",
            commentIconHeight: HOME_V2_COMMENT_ICON_HEIGHT,
            commentIconPosition: "left",
            commentIconWidth: HOME_V2_COMMENT_ICON_WIDTH,
            isBookmarkVisible: false,
            isCommentsVisible: true,
            isIconVisible: false,
          })}
        </ActionBar>
      </Content>
    </Container>
  );
};

Component.displayName = "ContentItemHomeSuperSplashLivePrimaryV2";

export const ContentItemHomeSuperSplashLivePrimaryV2 = withHighlightedHeadline(Component);
